import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient} from '@angular/common/http';
import { WorkflowAction } from 'src/app/models/WorkflowActions';
import { GetAdminsService } from 'src/app/services/getAdmins.service';

interface GroupedWorkflow {
  isOpen: boolean;
  data: WorkflowAction[];
  entityName?: string;
  workflowTitle:string;
  triggerEntityId?:number;
  triggerId?:number;
  createdAT?: string;
  departmentName:string;
  client?: string;
  startDate?: any;
}

@Component({
  selector: 'app-workflow-actions',
  templateUrl: './workflow-actions.component.html',
  styleUrls: ['./workflow-actions.component.css']
})
export class WorkflowActionsComponent implements OnInit {
  loadingWorkflow:boolean=true;
  selected:string='workflow-actions'
  mobileView: boolean = false;
  loading: boolean = false;
  status:string[]=['New', 'In Progress', 'Complete', 'Cancelled'];
  sorting: boolean = false;
  // Search filter properties
  searchWorkflowTitle: string = '';
  searchTriggerName: string = '';
  searchTriggerEntityId: string = '';
  searchStepTitle: string = '';
  searchDepartmentName: string = '';
  searchStepStatus: string = '';
  searchEntityName: string = '';
  currentSortColumn: string = '';
  sortDirection: boolean = true; // true for ascending, false for descending
  file:any;
  successMessage:string='';
  workflowToDelete: WorkflowAction | null = null;
  isModalVisible: boolean = false;
  admins: { name: string; email: string }[] = [];
  groupedWorkflows: { [key: string]: GroupedWorkflow } = {};
  isAscending: boolean = true;  
  sortProperty: string = '';
  areAdditionalHeadingsVisible: boolean = false;
  allGroupsExpanded: boolean = false;
  isTogglingAllGroups: boolean = false;
  ascending: boolean = true;
  sortOrder: 'asc' | 'desc' = 'asc';
  filteredWorkflows: { [key: string]: GroupedWorkflow } = {};
  searchAssignTo: string = '';
  actualWorkflowData:WorkflowAction[] = [];
  filteredData:WorkflowAction[] = [];
  searchStatus: string = '';
  selectedWorkflowSteps: WorkflowAction[] = [];
  isNoteModalVisible: boolean = false;
  selectedWorkflowForNote: GroupedWorkflow | null = null;

  constructor(private http: HttpClient,private cdr: ChangeDetectorRef, private admin:GetAdminsService) {}

  ngOnInit(): void {
    this.fetchAdmins()
    this.getAllWorkflows()
  }

  fetchAdmins(){
    this.admin.getAdmins().subscribe(
      (res: any) => {
        this.admins = res;
      },
      (error) => {
        console.error('Failed to determine admin status', error);
      }
    );
  }

  getAllWorkflows() {
    this.loadingWorkflow = true;
    const url = `${environment.apiConfig.uri}/admin/Candidates/GetAllWorkflowActions`;
    this.http.get(url).subscribe(
      (response: any) => {
        if (response.isSuccess) {
          const transformedData: WorkflowAction[] = response.data.map((workflow: any) => ({
            ...workflow,
            createdAT: this.convertToLocalTime(workflow.createdAT),
            dueDate: workflow.dueDate ? this.formatDate(workflow.dueDate) : null,
            startDate: workflow.startDate ? this.formatDate(workflow.startDate) : null,
            isSelected: false,
            note: workflow.note || null,
          }));
          this.actualWorkflowData=transformedData;
          this.groupedWorkflows = this.groupDataBytriggerId(transformedData);
          this.filteredWorkflows=this.groupedWorkflows;
        } else {
          console.error('Failed to fetch workflows:', response.message);
          this.filteredWorkflows = {}; // Reset to empty object on failure
        }
        this.loadingWorkflow = false;
      },
      (error) => {
        console.error('Error fetching workflows:', error);
        this.filteredWorkflows = {}; // Reset to empty object on error
        this.loadingWorkflow = false;
      }
    );
  }

  applyFilter() {
    // If no search terms, show all workflows
    if (!this.searchAssignTo?.trim() && !this.searchStatus?.trim()) {
      this.filteredWorkflows = this.groupedWorkflows;
      return;
    }
    // Start with all data
    let filteredData = [...this.actualWorkflowData];
    
    // Apply assignee filter if provided
    if (this.searchAssignTo?.trim()) {
      const searchTermAssignee = this.searchAssignTo.toLowerCase().trim();
      filteredData = filteredData.filter((item: any) => {
        if (searchTermAssignee === 'null' || searchTermAssignee === 'unassigned') {
          return item.assignTo === null;
        }
        return item.assignTo?.toLowerCase().includes(searchTermAssignee);
      });
    }
    
    // Apply status filter if provided
    if (this.searchStatus?.trim()) {
      const searchTermStatus = this.searchStatus.toLowerCase().trim();
      filteredData = filteredData.filter((item: any) => {
        return item.status?.toLowerCase().includes(searchTermStatus);
      });
    }
    
    // Regroup the filtered data
    this.filteredWorkflows = this.groupDataBytriggerId(filteredData);
  }

  onCheckboxChange(workflow: WorkflowAction): void {
    if (workflow.isSelected) {
      this.selectedWorkflowSteps.push(workflow);
    } else {
      this.selectedWorkflowSteps = this.selectedWorkflowSteps.filter(item => item !== workflow);
    }
  }

  groupDataBytriggerId(workflows: WorkflowAction[]): { [key: string]: GroupedWorkflow } {
    return workflows.reduce((groups: { [key: string]: GroupedWorkflow }, workflow: WorkflowAction) => {
      const groupKey = workflow.triggerId;
      // If group doesn't exist, create it
      if (!groups[groupKey]) {
        groups[groupKey] = {
          isOpen: false,
          data: [],
          entityName: workflow.entityName, // Store the entityName at the parent level
          workflowTitle: workflow.workflowTitle, // Store the workflowTitle at the parent level
          triggerEntityId:workflow.triggerEntityId,
          createdAT:workflow.createdAT,
          departmentName:workflow.departmentName,
          triggerId:workflow.triggerId,
          client: workflow.client,
          startDate: workflow.startDate
        };
      }
      
      // Add workflow to the group's data array
      groups[groupKey].data.push(workflow);
      return groups;
    }, {});
  }

  // Method to get the keys of groupedWorkflows
  getGroupKeys(): string[] {
    return Object.keys(this.filteredWorkflows);  // Return the keys as an array
  }

  toggleGroup(groupKey: string): void {
    const group = this.filteredWorkflows[groupKey];
    if (group) {
      group.isOpen = !group.isOpen;
      // Show additional headings if any group is open
      this.areAdditionalHeadingsVisible = Object.values(this.filteredWorkflows).some(group => group.isOpen);
    }
  }

  toggleAllGroups(): void {
    this.isTogglingAllGroups = true;
    this.cdr.detectChanges(); // Force spinner to show immediately
    this.allGroupsExpanded = !this.allGroupsExpanded;
    Object.keys(this.filteredWorkflows).forEach(groupKey => {
      this.filteredWorkflows[groupKey].isOpen = this.allGroupsExpanded;
    });
    this.areAdditionalHeadingsVisible = this.allGroupsExpanded;
    this.isTogglingAllGroups = false;
    this.cdr.detectChanges(); // Force icon to show after process
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    return `${year}-${month}-${day}`;
  }

  private convertToLocalTime(utcDate: string): string | Date {
    const localDate = new Date(`${utcDate}Z`);
    return localDate;
  }

  deleteworkflowAction(workflow:any){
    this.workflowToDelete = workflow;
    this.isModalVisible = true;
    this.successMessage='';
    this.cdr.detectChanges();
  }

  deleteSelectedWorkflowActions(): void {
    if (this.selectedWorkflowSteps.length > 0) {
      this.workflowToDelete = null; // Clear single selection
      this.isModalVisible = true;
      this.successMessage = '';
      this.cdr.detectChanges();
    }
  }

  confirmDelete() {
    if (this.workflowToDelete) { // Single deletion
      this.loading = true;
      const { id, stepId } = this.workflowToDelete;
      const url = `${environment.apiConfig.uri}/admin/Candidates/DeleteWorkFlowAction?Id=${id}&stepId=${stepId}`;
      this.http.delete(url).subscribe(
        (res: any) => {
          // Iterate over groupedWorkflows and remove the deleted workflow
          Object.keys(this.filteredWorkflows).forEach((key) => {
            const group = this.filteredWorkflows[key];
            group.data = group.data.filter(workflow => !(workflow.id === id && workflow.stepId === stepId));
            
            // Optionally remove empty groups if needed
            if (group.data.length === 0) {
              delete this.filteredWorkflows[key];
            }
          });
  
          this.successMessage = 'Workflow Action Deleted Successfully.';
          this.loading = false;
          this.isModalVisible = true;
          this.workflowToDelete = null; // Clear after deletion
        },
        (error) => {
          console.error('Error deleting workflow action:', error);
          this.loading = false;
          this.isModalVisible = true;
        }
      );
    } else if (this.selectedWorkflowSteps.length > 0) { // Bulk deletion
      this.loading = true;
      const payload = this.selectedWorkflowSteps.map(workflow => ({
        id: workflow.id,
        stepId: workflow.stepId
      }));
      const url = `${environment.apiConfig.uri}/admin/Candidates/BulkDeleteWorkFlowAction`;
      this.http.delete(url, { body: payload } as any).subscribe(
        (responses: any) => {
          // Remove deleted workflows from filteredWorkflows
          this.selectedWorkflowSteps.forEach(deletedWorkflow => {
            Object.keys(this.filteredWorkflows).forEach(groupKey => {
              const group = this.filteredWorkflows[groupKey];
              group.data = group.data.filter(workflow => !(workflow.id === deletedWorkflow.id && workflow.stepId === deletedWorkflow.stepId));
              if (group.data.length === 0) {
                delete this.filteredWorkflows[groupKey];
              }
            });
          });
          this.selectedWorkflowSteps = []; // Clear selected items
          this.successMessage = 'Selected Workflow Actions Deleted Successfully.';
          this.loading = false;
          this.isModalVisible = true;
        },
        (error) => {
          console.error('Error deleting selected workflow actions:', error);
          this.loading = false;
          this.isModalVisible = true;
        }
      );
    }
  }

  onCancel() {
    this.isModalVisible = false;
  }

  onClose() {
    this.isModalVisible = false;
  }

  openNoteModal(groupKey: string): void {
    const group = this.filteredWorkflows[groupKey];
    if (group) {
      this.selectedWorkflowForNote = group;
      this.isNoteModalVisible = true;
    }
  }

  saveWorkflowNote(note: string): void {
    if (this.selectedWorkflowForNote && this.selectedWorkflowForNote.triggerId) {
      const triggerId = this.selectedWorkflowForNote.triggerId;
      const url = `${environment.apiConfig.uri}/admin/Candidates/AddWorkFlowNote?triggerId=${triggerId}`;
      this.loading = true;
      this.http.post(url, `"${note}"`, { headers: { 'Content-Type': 'application/json' } }).subscribe(
        (response: any) => {
          if (response.isSuccess) {
            // Update the note in the local data
            if (this.selectedWorkflowForNote) {
              this.selectedWorkflowForNote.data.forEach(workflow => workflow.note = note);
            }
            this.successMessage = 'Note saved successfully.';
            this.loading = false;
            this.cdr.detectChanges(); // Ensure message is rendered
            setTimeout(() => {
              this.isNoteModalVisible = false;
              this.selectedWorkflowForNote = null; // Clear selected workflow for note
              this.successMessage = ''; // Clear success message after modal closes
            }, 1500); // Show message for 1.5 seconds
          } else {
            this.successMessage = 'Failed to save note.';
            this.loading = false;
            this.isNoteModalVisible = false;
            this.cdr.detectChanges();
          }
        },
        (error) => {
          console.error('Error saving note:', error);
          this.successMessage = 'Error saving note.';
          this.loading = false;
          this.isNoteModalVisible = false;
          this.cdr.detectChanges();
        }
      );
    }
  }

  onNoteModalClose(): void {
    this.isNoteModalVisible = false;
    this.selectedWorkflowForNote = null;
    this.successMessage = ''; // Clear any success/error messages
  }

  getFileStream(item: any, view: boolean = false) {
    const url = `${environment.apiConfig.uri}/admin/File/GetWorkFlowFileStream/${item.fileId}`;
    this.http.get(url).subscribe(
      (response: any) => {
        this.file = response.data || '';
        item.fileStream = this.file;
        if (view) {
          this.onClickView(item);
        } else {
          this.onClickDownload(item);
        }
      },
      (error) => {
        console.error('Error fetching file stream:', error);
      }
    );
}

onClickDownload(item: any) {
    if (!item.fileStream) {
        this.getFileStream(item);
    } else if (item.fileStream && item.fileStream.trim().length > 0) {
        const link = document.createElement('a');
        link.download = `${item.fileName}`;
        const source = `data:${this.getMimeType(item.fileName)};base64,${item.fileStream}`;
        link.href = source;
        link.click();
    }
}

onClickView(item: any) {
  if (!item.fileStream) {
      this.getFileStream(item, true);
  } else if (item.fileStream && item.fileStream.trim().length > 0) {
      const mimeType = this.getMimeType(item.fileName);
      if (mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
          this.onClickDownload(item);
      } else {
          const blob = this.dataURItoBlob(item);
          const file = new File([blob], item.fileName, {
              type: mimeType,
          });
          const url = window.URL.createObjectURL(file);
          window.open(url);
      }
  }
}

dataURItoBlob(item: any) {
    const byteString = window.atob(item.fileStream);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const int8Array = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
        int8Array[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([int8Array], {
        type: this.getMimeType(item.fileName),
    });
    return blob;
}

getMimeType(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
        case 'pdf': return 'application/pdf';
        case 'jpg': 
        case 'jpeg': return 'image/jpeg';
        case 'png': return 'image/png';
        case 'gif': return 'image/gif';
        case 'mp4': return 'video/mp4';
        case 'mov': return 'video/quicktime';
        case 'avi': return 'video/x-msvideo';
        case 'mp3': return 'audio/mpeg';
        case 'webp': return 'image/webp';
        case 'docx': return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        default: return 'application/octet-stream';
    }
}

  // applyFilter() {
  //   this.workFlowsData = this.transformedData.filter((item: { workflowTitle: string; entityName: string; triggerEntityId: { toString: () => string | string[]; }; title: string; departmentName: string; status: string; }) => {
  //     return (!this.searchWorkflowTitle || (item.workflowTitle && item.workflowTitle.toLowerCase().includes(this.searchWorkflowTitle.toLowerCase()))) &&
  //          (!this.searchEntityName || (item.entityName && item.entityName.toLowerCase().includes(this.searchEntityName.toLowerCase()))) &&
  //          (!this.searchTriggerEntityId || item.triggerEntityId.toString().includes(this.searchTriggerEntityId)) &&
  //          (!this.searchStepTitle || (item.title && item.title.toLowerCase().includes(this.searchStepTitle.toLowerCase()))) &&
  //          (!this.searchDepartmentName || (item.departmentName && item.departmentName.toLowerCase().includes(this.searchDepartmentName.toLowerCase()))) &&
  //          (!this.searchStepStatus || (item.status && item.status.toLowerCase().includes(this.searchStepStatus.toLowerCase())));;
  //   });
  // }

  updateStep(id: number, status: string, dueDate: string, assignTo:string) {
    let url = `${environment.apiConfig.uri}/admin/Candidates/UpdateStep?id=${id}&status=${encodeURIComponent(status)}&dueDate=${dueDate}&assignTo=${encodeURIComponent(assignTo)}`;
    this.http.post(url, {}).subscribe(response => {
    }, error => {
      console.error('Error updating step', error);
    });
  }

  onAssignTo(item:any){
    this.updateStep(item.id, item.status?item.status:'', item.dueDate?item.dueDate:'', item.assignTo?item.assignTo:'');
  }

  onDueDateChange(item: any) {
    this.updateStep(item.id, item.status?item.status:'', item.dueDate?item.dueDate:'', item.assignTo?item.assignTo:'');
  }

  onStatusChange(item: any) {
    this.updateStep(item.id, item.status?item.status:'', item.dueDate?item.dueDate:'', item.assignTo?item.assignTo:'');
  }

  onKeyDown(event: KeyboardEvent) {
    if (['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '.'].indexOf(event.key) !== -1) {
      event.preventDefault();
    }
  }

  sort(property: 'entityName' | 'workflowTitle' | 'createdAT' | 'departmentName' | 'client' | 'startDate'): void {
    const groupedWorkflowsArray: GroupedWorkflow[] = Object.values(this.filteredWorkflows);
    groupedWorkflowsArray.sort((a, b) => {
      // Special handling for createdAT (date comparison)
      if (property === 'createdAT' || property === 'startDate') {
        const dateA = new Date(a[property] || '');
        const dateB = new Date(b[property] || '');
        return this.sortOrder === 'asc' 
          ? dateA.getTime() - dateB.getTime()
          : dateB.getTime() - dateA.getTime();
      }
      // String comparison for other properties
      const valueA = a[property]?.toString().trim().toLowerCase() || '';
      const valueB = b[property]?.toString().trim().toLowerCase() || '';
      if (valueA < valueB) return this.sortOrder === 'asc' ? -1 : 1;
      if (valueA > valueB) return this.sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    // Toggle sort order
    this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';

    // Recreate the object
    this.filteredWorkflows = groupedWorkflowsArray.reduce((acc: { [key: string]: GroupedWorkflow }, item: GroupedWorkflow) => {
      const uniqueKey = `${item.triggerId}-${item.workflowTitle}`;
      acc[uniqueKey] = item as GroupedWorkflow;
      return acc;
    }, {} as { [key: string]: GroupedWorkflow });
}
  
}
